import React, { useEffect, useMemo, useState } from "react";
import { useTheme } from "../../contexts/ThemeContext";
import { useAuth } from "../../contexts/AuthContext";
import { useEmpresa } from "../../contexts/EmpresaContext";
import { nivelUsuario, pode } from "../../utils/permissoes";
import { NIVEIS } from "../../config/niveis";
import { PERMISSOES_DEFAULT } from "../../config/permissoesDefault";
import { db } from "../../config/firebase";
import { doc, getDoc, updateDoc, collection, getDocs, query, where, deleteField, serverTimestamp } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "../../config/firebase";
import { mergePermissoes, flattenPermissoes, setDeepLeaf } from "../../utils/permissoesOverrides";
import {
  apiGetAiConfig,
  apiSaveAiConfig,
  apiDeleteAiKey,
  apiTestAi,
} from "../../services/aiApi";
import {
  apiGetPermissoesGrupo,
  apiInitPermissoesGrupo,
  apiSavePermissoesGrupo,
  apiResetPermissoesGrupo,
} from "../../services/opcoesPermissoesApi";
import {
  apiGetRelatoriosGrupo,
  apiUploadLogoRelatorioGrupo,
} from "../../services/opcoesRelatoriosApi";
import {
  apiGetMapsConfig,
  apiSaveMapsKey,
  apiDeleteMapsKey,
} from "../../services/mapsConfigApi";
import {
  WHATSAPP_DEFAULT,
  apiGetWhatsAppConfig,
  apiSaveWhatsAppConfig,
  apiClearWhatsAppSecrets,
} from "../../services/whatsappConfigApi";
import LayoutDesktop from "../../layouts/LayoutDesktop";
import "./OpcoesPage.css";

const temas = [
  { id: "tema-a", nome: "Escuro Azul", tipo: "dark", cor: "#1e3a5f" },
  { id: "tema-b", nome: "Claro Bege", tipo: "light", cor: "#f5f0e6" },
  { id: "tema-c", nome: "Escuro Cinza", tipo: "dark", cor: "#2d2d2d" },
  { id: "tema-d", nome: "Claro Branco", tipo: "light", cor: "#ffffff" },
];

// Ordem e labels exibidos na tela de permissões (mantém 0..6 por baixo)
const NIVEIS_UI = [
  { n: NIVEIS.DASHBOARD, label: "DashboardTV" },
  { n: NIVEIS.KIOSK, label: "Kiosk" },
  { n: NIVEIS.CLIENTE, label: "Cliente" },
  { n: NIVEIS.TECNICO, label: "Técnico" },
  { n: NIVEIS.TECNICO_SENIOR, label: "Téc Sênior" },
  { n: NIVEIS.ADMIN, label: "Administrador" },
  { n: NIVEIS.GESTOR, label: "Gestor" },
];

const RELATORIO_EMPRESA_DEFAULT = {
  empresaNome: "Ramalho ProFit",
  empresaSubtitulo: "Montagem & Manutenção de Equipamentos Fitness",
  empresaCnpj: "29.252.847/0001-45",
  empresaTelefone: "(11) 95679-8125",
  empresaInstagram: "@ramalhoprofit",
  empresaEmail: "contato@ramalhoprofit.com.br",
  empresaSite: "www.ramalhoprofit.com.br",
  empresaEndereco: "São Paulo - SP",
};

const RELATORIO_EMAIL_DEFAULT = {
  emailFrom: "",
  emailTemplateBase:
    "Olá, {{clienteNome}}!\n\nSegue em anexo a {{tipoDocumento}} referente à unidade {{academiaNome}}.\n\nFico à disposição para qualquer dúvida.",
  emailTemplateInstalacao:
    "Encaminhamos a proposta de instalação para validação.\n\nResumo:\n- Cliente: {{clienteNome}}\n- Unidade: {{academiaNome}}\n- Valor total: {{valorTotal}}\n\nApós aprovação, seguimos com o agendamento.",
  emailTemplateOs:
    "Encaminhamos a proposta de Ordem de Serviço para validação.\n\nResumo:\n- Cliente: {{clienteNome}}\n- Unidade: {{academiaNome}}\n- Valor total: {{valorTotal}}",
  emailTemplateContrato:
    "Encaminhamos a proposta de contrato para validação.\n\nResumo:\n- Cliente: {{clienteNome}}\n- Tipo de contrato: {{contratoTipo}}\n- Início: {{dataInicial}}",
};

function validarEmailRemetente(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";

  const emailOnly = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const nomeComEmail = /^.+<[^\s@]+@[^\s@]+\.[^\s@]+>$/;

  if (emailOnly.test(raw) || nomeComEmail.test(raw)) return "";
  return "Formato inválido. Use email@dominio.com ou Nome <email@dominio.com>.";
}

// Estados possíveis dos "quadradinhos" da matriz
// - baseAllow (lvl >= nivelMinimo): mostra ✅ automaticamente
// - override: P (próprias) ou X (exceção/nega)
function computePermCellState({ baseAllow, override }) {
  const ov = (override || "").toLowerCase();
  if (ov === "excecao" || ov === "exceção" || ov === "deny") return "DENY"; // X
  if (ov === "proprias" || ov === "próprias" || ov === "own") return "OWN"; // P
  // compat: caso exista "todas" legado, trata como permitido
  if (ov === "todas" || ov === "all") return "ALLOW";
  return baseAllow ? "ALLOW" : "NONE";
}

function nextPermCellOverride({ baseAllow, currentOverride }) {
  const cur = (currentOverride || "").toLowerCase();
  // Ciclo:
  // - Se está vazio (ou "todas"), vai para P
  // - P -> X
  // - X -> limpa (volta pro padrão: ✅ se baseAllow, vazio se não)
  if (!cur || cur === "todas" || cur === "all") return "proprias";
  if (cur === "proprias" || cur === "próprias" || cur === "own") return "excecao";
  if (cur === "excecao" || cur === "exceção" || cur === "deny") return "";
  // fallback
  return baseAllow ? "" : "";
}

function OpcoesPage() {
  const { tema, alterarTema } = useTheme();
  const { usuario } = useAuth();
  const { empresaAtual, modoGlobal } = useEmpresa();

  // Permissões por aba (baseadas na matriz/overrides, não só no nível fixo)
  const podeAcessarOpcoes = useMemo(
    () => pode(usuario, "gerenciar", "opcoes", "acessa"),
    [usuario]
  );
  const podeAbaAparencia = useMemo(
    () => pode(usuario, "gerenciar", "opcoes", "aparencia"),
    [usuario]
  );
  const podeAbaIA = useMemo(() => pode(usuario, "gerenciar", "opcoes", "ia"), [usuario]);
  const podeAbaRelatorios = useMemo(
    () => pode(usuario, "gerenciar", "opcoes", "relatorios"),
    [usuario]
  );
  const podeAbaWhatsApp = useMemo(
    () => pode(usuario, "gerenciar", "opcoes", "whatsapp"),
    [usuario]
  );
  const podeAbaPermissoes = useMemo(
    () => pode(usuario, "gerenciar", "opcoes", "permissoes"),
    [usuario]
  );

  // Aliases (compatibilidade / legibilidade)
  const podeConfigurarIA = podeAbaIA;
  const podeConfigurarRelatorios = podeAbaRelatorios;
  const podeConfigurarWhatsApp = podeAbaWhatsApp;
  const podeGerenciarPermissoes = podeAbaPermissoes;


  const [aba, setAba] = useState("aparencia");

  // Garante que usuários sem permissão não "caiam" em abas avançadas (ex.: via estado antigo)
  useEffect(() => {
    const firstAllowed =
      (podeAbaAparencia && "aparencia") ||
      (podeAbaIA && "ia") ||
      (podeAbaRelatorios && "relatorios") ||
      (podeAbaWhatsApp && "whatsapp") ||
      (podeAbaPermissoes && "permissoes") ||
      "aparencia";

    if (aba === "aparencia" && !podeAbaAparencia) setAba(firstAllowed);
    if (aba === "ia" && !podeAbaIA) setAba(firstAllowed);
    if (aba === "relatorios" && !podeAbaRelatorios) setAba(firstAllowed);
    if (aba === "whatsapp" && !podeAbaWhatsApp) setAba(firstAllowed);
    if (aba === "permissoes" && !podeAbaPermissoes) setAba(firstAllowed);
  }, [aba, podeAbaAparencia, podeAbaIA, podeAbaRelatorios, podeAbaWhatsApp, podeAbaPermissoes]);


  const empresaId = empresaAtual?.id || "";
  const grupoId = usuario?.grupoId || "";

  const apiBase = useMemo(
    () => String(import.meta?.env?.VITE_API_BASE || "").replace(/\/$/, ""),
    []
  );
  const whatsappWebhookUrl = useMemo(() => {
    const origin = (typeof window !== "undefined" && window.location?.origin) ? window.location.origin : "";
    return `${apiBase || origin}/api/whatsapp/webhook`;
  }, [apiBase]);

  // ===================== Permissões (avançado) =====================
  const defaultRows = useMemo(() => flattenPermissoes(PERMISSOES_DEFAULT), []);
  const [permLoading, setPermLoading] = useState(false);
  const [permError, setPermError] = useState("");
  const [permMsg, setPermMsg] = useState("");
  const [permSearch, setPermSearch] = useState("");
  const [permOverrides, setPermOverrides] = useState({});
  const [permGrupoOverrides, setPermGrupoOverrides] = useState({});
  const [permAlvo, setPermAlvo] = useState("GRUPO"); // "GRUPO" | "USUARIO"
  const [permUserId, setPermUserId] = useState("");
  const [permUsers, setPermUsers] = useState([]);
  const [permUserDocExists, setPermUserDocExists] = useState(false);

  const [permEdit, setPermEdit] = useState({});
  const [permDocExists, setPermDocExists] = useState(true);

  const baseRows = useMemo(() => {
    const base = (permAlvo === "USUARIO")
      ? mergePermissoes(PERMISSOES_DEFAULT, permGrupoOverrides)
      : PERMISSOES_DEFAULT;
    return flattenPermissoes(base);
  }, [permAlvo, permGrupoOverrides]);

  const effectiveRows = useMemo(() => {
    const base = (permAlvo === "USUARIO")
      ? mergePermissoes(PERMISSOES_DEFAULT, permGrupoOverrides)
      : PERMISSOES_DEFAULT;
    const merged = mergePermissoes(base, permOverrides);
    return flattenPermissoes(merged);
  }, [permAlvo, permGrupoOverrides, permOverrides]);

  useEffect(() => {
    // Inicializa o estado editável a partir do efetivo
    const map = {};
    for (const r of effectiveRows) {
      map[r.id] = {
        nivelMinimo: Number(r.nivelMinimo ?? 0),
        escopo: r.escopo || {},
      };
    }
    setPermEdit(map);
  }, [effectiveRows]);

  useEffect(() => {
    setPermError("");
    setPermMsg("");
    if (!podeGerenciarPermissoes) return;
    if (!grupoId) return;

    let alive = true;
    setPermLoading(true);
    apiGetPermissoesGrupo({ grupoId })
      .then((cfg) => {
        if (!alive) return;
        const ov = (cfg.overrides || {});
        setPermGrupoOverrides(ov);
        if (permAlvo === "GRUPO") setPermOverrides(ov);
        setPermDocExists(!!cfg.exists);
      })
      .catch((e) => {
        if (!alive) return;
        setPermError(String(e.message || e));
      })
      .finally(() => {
        if (!alive) return;
        setPermLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [grupoId, permAlvo, podeGerenciarPermissoes]);

  
  // ---- Permissões por usuário (overrides individuais) ----
  useEffect(() => {
    // Ao alternar o alvo, mantém o editor consistente
    if (permAlvo === "GRUPO") {
      setPermUserId("");
      setPermUserDocExists(false);
      setPermOverrides(permGrupoOverrides || {});
    } else {
      // USUARIO
      if (!permUserId) {
        setPermOverrides({});
      }
    }
  }, [permAlvo, permGrupoOverrides]); 

  useEffect(() => {
    // Carregar lista de usuários do grupo para seleção
    if (!podeGerenciarPermissoes) return;
    if (permAlvo !== "USUARIO") return;
    if (!grupoId) return;

    let alive = true;
    (async () => {
      try {
        const q = query(collection(db, "usuarios"), where("grupoId", "==", grupoId));
        const snap = await getDocs(q);
        if (!alive) return;
        const list = snap.docs.map((d) => {
          const data = d.data() || {};
          return {
            id: d.id,
            nome: data.nome || data.displayName || data.email || d.id,
            nivel: data.nivel,
          };
        });
        list.sort((a, b) => String(a.nome).localeCompare(String(b.nome)));
        setPermUsers(list);
      } catch (e) {
        // silencioso (não bloqueia a tela)
        console.warn("Falha ao carregar usuários para permissões:", e);
      }
    })();

    return () => { alive = false; };
  }, [permAlvo, grupoId, podeGerenciarPermissoes]);

  useEffect(() => {
    // Carregar overrides do usuário selecionado
    if (!podeGerenciarPermissoes) return;
    if (permAlvo !== "USUARIO") return;
    if (!grupoId) return;
    if (!permUserId) return;

    let alive = true;
    (async () => {
      try {
        setPermLoading(true);
        const ref = doc(db, "usuarios", permUserId);
        const snap = await getDoc(ref);
        if (!alive) return;
        const data = snap.exists() ? (snap.data() || {}) : {};
        const ov = data.permissoesUserOverrides || data.permissoesUsuarioOverrides || data.permissoesOverridesUsuario || {};
        setPermOverrides(ov || {});
        setPermUserDocExists(!!(ov && Object.keys(ov).length > 0));
        setPermError("");
        setPermMsg("");
      } catch (e) {
        if (!alive) return;
        setPermError(String(e.message || e));
      } finally {
        if (!alive) return;
        setPermLoading(false);
      }
    })();

    return () => { alive = false; };
  }, [permAlvo, permUserId, grupoId, podeGerenciarPermissoes]);

const permFilteredRows = useMemo(() => {
    const q = (permSearch || "").trim().toLowerCase();
    if (!q) return effectiveRows;
    return effectiveRows.filter((r) =>
      (`${r.modulo} ${r.recurso} ${r.acao} ${r.id}`)
        .toLowerCase()
        .includes(q)
    );
  }, [effectiveRows, permSearch]);

  // ===================== Permissões (UI tipo "Defeitos") =====================
  const [permNivelTeste, setPermNivelTeste] = useState(NIVEIS.CLIENTE);
  const [permPickLeft, setPermPickLeft] = useState({});
  const [permPickRight, setPermPickRight] = useState({});
  const [permFocusId, setPermFocusId] = useState("");

  // Ajusta o nível de teste automaticamente ao escolher um usuário
  useEffect(() => {
    if (permAlvo !== "USUARIO") return;
    if (!permUserId) return;
    const u = (permUsers || []).find((x) => String(x.id) === String(permUserId));
    const n = Number(u?.nivel);
    if (!Number.isNaN(n) && n >= 0) setPermNivelTeste(n);
  }, [permAlvo, permUserId, permUsers]);

  const defaultMap = useMemo(() => {
    const m = {};
    for (const r of defaultRows) {
      m[r.id] = { nivelMinimo: Number(r.nivelMinimo ?? 0), escopo: r.escopo || {} };
    }
    return m;
  }, [defaultRows]);

  const baseMap = useMemo(() => {
    const m = {};
    for (const r of baseRows) {
      m[r.id] = { nivelMinimo: Number(r.nivelMinimo ?? 0), escopo: r.escopo || {} };
    }
    return m;
  }, [baseRows]);

  const effectiveMap = useMemo(() => {
    const m = {};
    for (const r of effectiveRows) {
      m[r.id] = { nivelMinimo: Number(r.nivelMinimo ?? 0), escopo: r.escopo || {} };
    }
    return m;
  }, [effectiveRows]);

  const rowById = useMemo(() => {
    const m = {};
    for (const r of effectiveRows) m[r.id] = r;
    return m;
  }, [effectiveRows]);

  function permOrigin(id) {
    const eff = effectiveMap[id] || { nivelMinimo: 0, escopo: {} };
    const base = baseMap[id] || defaultMap[id] || { nivelMinimo: 0, escopo: {} };
    const def = defaultMap[id] || { nivelMinimo: 0, escopo: {} };

    const sameEscopo = (a, b) => {
      const ka = Object.keys(a || {}).sort();
      const kb = Object.keys(b || {}).sort();
      if (ka.length !== kb.length) return false;
      for (let i = 0; i < ka.length; i++) {
        if (ka[i] !== kb[i]) return false;
        if (String(a[ka[i]]) !== String(b[kb[i]])) return false;
      }
      return true;
    };

    const sameEffBase = (Number(eff.nivelMinimo) === Number(base.nivelMinimo)) && sameEscopo(eff.escopo, base.escopo);
    const sameBaseDef = (Number(base.nivelMinimo) === Number(def.nivelMinimo)) && sameEscopo(base.escopo, def.escopo);

    if (permAlvo === "USUARIO") {
      if (!sameEffBase) return "USUÁRIO";
      if (!sameBaseDef) return "GRUPO";
      return "DEFAULT";
    }
    // GRUPO
    if (!sameBaseDef) return "GRUPO";
    return "DEFAULT";
  }

  function permAllowedForNivel(id, nivel) {
    const ed = permEdit[id] || effectiveMap[id] || { nivelMinimo: 0, escopo: {} };
    const nivelMin = Number(ed.nivelMinimo ?? 0);
    const baseAllow = Number(nivel) >= nivelMin;
    const override = (ed.escopo || {})[String(nivel)] || "";
    const state = computePermCellState({ baseAllow, override });
    return state === "ALLOW" || state === "OWN";
  }

  const permLeftRows = useMemo(() => {
    const q = (permSearch || "").trim().toLowerCase();
    return effectiveRows
      .filter((r) => !permAllowedForNivel(r.id, permNivelTeste))
      .filter((r) => !q || (`${r.modulo} ${r.recurso} ${r.acao} ${r.id}`).toLowerCase().includes(q));
  }, [effectiveRows, permSearch, permNivelTeste, permEdit]);

  const permRightRows = useMemo(() => {
    const q = (permSearch || "").trim().toLowerCase();
    return effectiveRows
      .filter((r) => permAllowedForNivel(r.id, permNivelTeste))
      .filter((r) => !q || (`${r.modulo} ${r.recurso} ${r.acao} ${r.id}`).toLowerCase().includes(q));
  }, [effectiveRows, permSearch, permNivelTeste, permEdit]);

  const grantPermIds = (ids) => {
    if (!ids || ids.length === 0) return;
    setPermEdit((prev) => {
      const next = { ...prev };
      for (const id of ids) {
        const cur = next[id] || prev[id] || effectiveMap[id] || { nivelMinimo: 0, escopo: {} };
        const nivelMin = Number(cur.nivelMinimo ?? 0);
        const escopo = { ...(cur.escopo || {}) };

        // Se houver exceção negando exatamente este nível, remove
        const k = String(permNivelTeste);
        const ov = String(escopo[k] || "").toLowerCase();
        if (ov === "excecao" || ov === "exceção" || ov === "deny") delete escopo[k];

        next[id] = {
          nivelMinimo: nivelMin > permNivelTeste ? Number(permNivelTeste) : nivelMin,
          escopo,
        };
      }
      return next;
    });
    setPermPickLeft({});
  };

  const revokePermIds = (ids) => {
    if (!ids || ids.length === 0) return;
    setPermEdit((prev) => {
      const next = { ...prev };
      for (const id of ids) {
        const base = baseMap[id] || { nivelMinimo: 0, escopo: {} };
        next[id] = {
          nivelMinimo: Number(base.nivelMinimo ?? 0),
          escopo: { ...(base.escopo || {}) },
        };
      }
      return next;
    });
    setPermPickRight({});
  };

  const grantPicked = () => grantPermIds(Object.keys(permPickLeft || {}).filter((k) => permPickLeft[k]));
  const revokePicked = () => revokePermIds(Object.keys(permPickRight || {}).filter((k) => permPickRight[k]));

  const grantAllFiltered = () => grantPermIds(permLeftRows.map((r) => r.id));
  const revokeAllFiltered = () => revokePermIds(permRightRows.map((r) => r.id));


  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState("");
  const [aiMsg, setAiMsg] = useState("");
  const [aiEnabled, setAiEnabled] = useState(false);
  const [aiHasKey, setAiHasKey] = useState(false);
  const [aiModel, setAiModel] = useState("gpt-5-mini");
  const [aiKeyInput, setAiKeyInput] = useState("");

  // ===================== Relatórios (logo) =====================
  const [relLoading, setRelLoading] = useState(false);
  const [relError, setRelError] = useState("");
  const [relMsg, setRelMsg] = useState("");
  const [relCfg, setRelCfg] = useState(null);
  const [relEmpresa, setRelEmpresa] = useState(RELATORIO_EMPRESA_DEFAULT);
  const [relEmpresaDraft, setRelEmpresaDraft] = useState(RELATORIO_EMPRESA_DEFAULT);
  const [relEmpresaSaving, setRelEmpresaSaving] = useState(false);
  const [relEmail, setRelEmail] = useState(RELATORIO_EMAIL_DEFAULT);
  const [relEmailDraft, setRelEmailDraft] = useState(RELATORIO_EMAIL_DEFAULT);
  const [relEmailSaving, setRelEmailSaving] = useState(false);
  const relEmailFromError = useMemo(
    () => validarEmailRemetente(relEmailDraft.emailFrom),
    [relEmailDraft.emailFrom]
  );
  
  // Endereço de origem para cálculo de distância
  const [enderecoOrigem, setEnderecoOrigem] = useState("");
  const [enderecoOrigemTemp, setEnderecoOrigemTemp] = useState("");
  const [enderecoLoading, setEnderecoLoading] = useState(false);
  const [enderecoError, setEnderecoError] = useState("");
  const [enderecoMsg, setEnderecoMsg] = useState("");
  const [valorKm, setValorKm] = useState("1.20");
  const [valorKmTemp, setValorKmTemp] = useState("1.20");
  const [valorKmLoading, setValorKmLoading] = useState(false);
  const [valorKmError, setValorKmError] = useState("");
  const [valorKmMsg, setValorKmMsg] = useState("");
  const [mapsHasKey, setMapsHasKey] = useState(false);
  const [mapsKeyInput, setMapsKeyInput] = useState("");
  const [mapsKeyLoading, setMapsKeyLoading] = useState(false);
  const [mapsKeyError, setMapsKeyError] = useState("");
  const [mapsKeyMsg, setMapsKeyMsg] = useState("");

  // ===================== WhatsApp (integração) =====================
  const [waLoading, setWaLoading] = useState(false);
  const [waError, setWaError] = useState("");
  const [waMsg, setWaMsg] = useState("");
  const [waCfg, setWaCfg] = useState({ ...WHATSAPP_DEFAULT });
  const [waDraft, setWaDraft] = useState({ ...WHATSAPP_DEFAULT });

  const [waTestTo, setWaTestTo] = useState("");
  const [waTestText, setWaTestText] = useState("Teste Mamute Fit ✅");
  const [waTestLoading, setWaTestLoading] = useState(false);
  const [waTestError, setWaTestError] = useState("");
  const [waTestMsg, setWaTestMsg] = useState("");

  useEffect(() => {
    if (!podeConfigurarRelatorios) return;
    if (!grupoId) {
      setRelCfg(null);
      return;
    }

    let alive = true;
    setRelLoading(true);
    setRelError("");
    setRelMsg("");
    apiGetRelatoriosGrupo(grupoId)
      .then((cfg) => {
        if (!alive) return;
        setRelCfg(cfg || null);
      })
      .catch((e) => {
        if (!alive) return;
        setRelError(String(e.message || e));
      })
      .finally(() => {
        if (!alive) return;
        setRelLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [grupoId, podeConfigurarRelatorios]);

  // Carregar endereço de origem do grupo
  useEffect(() => {
    if (!podeConfigurarRelatorios) return;
    if (!grupoId) {
      setEnderecoOrigem("");
      setEnderecoOrigemTemp("");
      return;
    }

    let alive = true;
    setEnderecoLoading(true);
    setEnderecoError("");
    
    const grupoRef = doc(db, "grupos", grupoId);
    getDoc(grupoRef)
      .then((snap) => {
        if (!alive) return;
        if (snap.exists()) {
          const data = snap.data();
          const endereco = data.enderecoOrigem || "";
          const kmValue = data.valorKmDeslocamento != null ? String(data.valorKmDeslocamento) : "1.20";
          const relatoriosRaw = data.relatorios || {};
          const relatoriosEmpresa = {
            empresaNome: String(relatoriosRaw.empresaNome || RELATORIO_EMPRESA_DEFAULT.empresaNome),
            empresaSubtitulo: String(relatoriosRaw.empresaSubtitulo || RELATORIO_EMPRESA_DEFAULT.empresaSubtitulo),
            empresaCnpj: String(relatoriosRaw.empresaCnpj || RELATORIO_EMPRESA_DEFAULT.empresaCnpj),
            empresaTelefone: String(relatoriosRaw.empresaTelefone || RELATORIO_EMPRESA_DEFAULT.empresaTelefone),
            empresaInstagram: String(relatoriosRaw.empresaInstagram || RELATORIO_EMPRESA_DEFAULT.empresaInstagram),
            empresaEmail: String(relatoriosRaw.empresaEmail || RELATORIO_EMPRESA_DEFAULT.empresaEmail),
            empresaSite: String(relatoriosRaw.empresaSite || RELATORIO_EMPRESA_DEFAULT.empresaSite),
            empresaEndereco: String(relatoriosRaw.empresaEndereco || RELATORIO_EMPRESA_DEFAULT.empresaEndereco),
          };
          const relatoriosEmail = {
            emailFrom: String(relatoriosRaw.emailFrom || RELATORIO_EMAIL_DEFAULT.emailFrom),
            emailTemplateBase: String(relatoriosRaw.emailTemplateBase || RELATORIO_EMAIL_DEFAULT.emailTemplateBase),
            emailTemplateInstalacao: String(relatoriosRaw.emailTemplateInstalacao || RELATORIO_EMAIL_DEFAULT.emailTemplateInstalacao),
            emailTemplateOs: String(relatoriosRaw.emailTemplateOs || RELATORIO_EMAIL_DEFAULT.emailTemplateOs),
            emailTemplateContrato: String(relatoriosRaw.emailTemplateContrato || RELATORIO_EMAIL_DEFAULT.emailTemplateContrato),
          };
          setEnderecoOrigem(endereco);
          setEnderecoOrigemTemp(endereco);
          setValorKm(kmValue);
          setValorKmTemp(kmValue);
          setRelEmpresa(relatoriosEmpresa);
          setRelEmpresaDraft(relatoriosEmpresa);
          setRelEmail(relatoriosEmail);
          setRelEmailDraft(relatoriosEmail);
        }
      })
      .catch((e) => {
        if (!alive) return;
        setEnderecoError(String(e.message || e));
      })
      .finally(() => {
        if (!alive) return;
        setEnderecoLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [grupoId, podeConfigurarRelatorios]);

  useEffect(() => {
    if (!podeConfigurarRelatorios) return;
    if (!grupoId) {
      setMapsHasKey(false);
      return;
    }

    let alive = true;
    setMapsKeyLoading(true);
    setMapsKeyError("");
    setMapsKeyMsg("");

    apiGetMapsConfig(grupoId)
      .then((cfg) => {
        if (!alive) return;
        setMapsHasKey(!!cfg?.hasKey);
      })
      .catch((e) => {
        if (!alive) return;
        setMapsKeyError(String(e.message || e));
      })
      .finally(() => {
        if (!alive) return;
        setMapsKeyLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [grupoId, podeConfigurarRelatorios]);

  // Carrega config do WhatsApp (por grupo)
  useEffect(() => {
    if (!podeConfigurarWhatsApp) return;
    if (!grupoId) {
      setWaCfg({ ...WHATSAPP_DEFAULT });
      setWaDraft({ ...WHATSAPP_DEFAULT });
      return;
    }

    let alive = true;
    setWaLoading(true);
    setWaError("");
    setWaMsg("");

    apiGetWhatsAppConfig(grupoId)
      .then((resp) => {
        if (!alive) return;
        const cfg = resp?.config || { ...WHATSAPP_DEFAULT };
        setWaCfg(cfg);
        setWaDraft(cfg);
      })
      .catch((e) => {
        if (!alive) return;
        setWaError(String(e.message || e));
      })
      .finally(() => {
        if (!alive) return;
        setWaLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [grupoId, podeConfigurarWhatsApp]);

  // ===================== Permissões (avançado) =====================
  const handlePermSave = async () => {
    setPermError("");
    setPermMsg("");
    if (!grupoId) {
      setPermError("Usuário sem grupoId.");
      return;
    }
    try {
      setPermLoading(true);

      // Mapa do default para comparação
      const defaultMap = {};
      for (const r of baseRows) {
        defaultMap[r.id] = {
          nivelMinimo: Number(r.nivelMinimo ?? 0),
          escopo: r.escopo || {},
        };
      }

      const overrides = {};

      const sameEscopo = (a, b) => {
        const ka = Object.keys(a || {}).sort();
        const kb = Object.keys(b || {}).sort();
        if (ka.length !== kb.length) return false;
        for (let i = 0; i < ka.length; i++) {
          if (ka[i] !== kb[i]) return false;
          if (String(a[ka[i]]) !== String(b[kb[i]])) return false;
        }
        return true;
      };

      for (const r of baseRows) {
        const def = defaultMap[r.id] || { nivelMinimo: 0, escopo: {} };
        const ed = permEdit[r.id] || def;

        const nivelMinimo = Number(ed.nivelMinimo ?? def.nivelMinimo ?? 0);

        // Normaliza escopo (somente valores válidos)
        const escopoNorm = {};
        const escopoRaw = ed.escopo || {};
        for (const [k, v] of Object.entries(escopoRaw)) {
          if (v === "proprias" || v === "todas" || v === "excecao" || v === "exceção") {
            escopoNorm[String(k)] = v === "exceção" ? "excecao" : v;
          }
        }

        const defEscopoNorm = {};
        for (const [k, v] of Object.entries(def.escopo || {})) {
          if (v === "proprias" || v === "todas" || v === "excecao" || v === "exceção") {
            defEscopoNorm[String(k)] = v === "exceção" ? "excecao" : v;
          }
        }

        const mudouNivel = nivelMinimo !== Number(def.nivelMinimo ?? 0);
        const mudouEscopo = !sameEscopo(escopoNorm, defEscopoNorm);

        if (!mudouNivel && !mudouEscopo) continue;

        const leaf = { nivelMinimo };

        // Se o default tinha escopo e o usuário removeu tudo, salva {} para sobrescrever.
        if (Object.keys(escopoNorm).length > 0 || Object.keys(defEscopoNorm).length > 0) {
          leaf.escopo = escopoNorm;
        }

        setDeepLeaf(overrides, [r.modulo, r.recurso, r.acao], leaf);
      }

            if (permAlvo === "USUARIO") {
        if (!permUserId) throw new Error("Selecione um usuário para salvar permissões individuais.");
        const uRef = doc(db, "usuarios", permUserId);
        await updateDoc(uRef, { permissoesUserOverrides: overrides });
        setPermOverrides(overrides);
        setPermUserDocExists(Object.keys(overrides || {}).length > 0);
        setPermMsg("Permissões salvas para o usuário selecionado. (Ele precisa atualizar o app para aplicar)");
      } else {
        await apiSavePermissoesGrupo({
          grupoId,
          overrides,
          updatedBy: usuario?.uid || null,
        });

        setPermOverrides(overrides);
        setPermGrupoOverrides(overrides);
        setPermDocExists(true);
        setPermMsg("Permissões salvas. (Aplicação é imediata no sistema)");
      }
    } catch (e) {
      setPermError(String(e.message || e));
    } finally {
      setPermLoading(false);
    }
  };

  const handlePermInit = async () => {
    setPermError("");
    setPermMsg("");
    if (permAlvo === "USUARIO") {
      setPermError("Inicialização só existe para permissões do grupo.");
      return;
    }
    if (!grupoId) {
      setPermError("Usuário sem grupoId.");
      return;
    }
    try {
      setPermLoading(true);
      await apiInitPermissoesGrupo({
        grupoId,
        createdBy: usuario?.uid || null,
      });
      setPermDocExists(true);
      setPermMsg("Permissões inicializadas para este grupo. Agora você já pode salvar alterações.");
    } catch (e) {
      setPermError(String(e.message || e));
    } finally {
      setPermLoading(false);
    }
  };

  const handlePermReset = async () => {
    setPermError("");
    setPermMsg("");
    if (!grupoId) {
      setPermError("Usuário sem grupoId.");
      return;
    }
    try {
      setPermLoading(true);
      if (permAlvo === "USUARIO") {
        if (!permUserId) {
          throw new Error("Selecione um usuário para restaurar.");
        }
        const uRef = doc(db, "usuarios", permUserId);
        await updateDoc(uRef, { permissoesUserOverrides: deleteField() });
        setPermOverrides({});
        setPermUserDocExists(false);
        setPermMsg("Permissões individuais removidas. (O usuário volta a usar as permissões do grupo)");
      } else {
        await apiResetPermissoesGrupo({ grupoId });
        setPermOverrides({});
        setPermGrupoOverrides({});
        setPermDocExists(false);
        setPermMsg("Permissões restauradas para o padrão.");
      }
    } catch (e) {
      setPermError(String(e.message || e));
    } finally {
      setPermLoading(false);
    }
  };

  const handleAiTest = async () => {
    setAiError("");
    setAiMsg("");
    if (!empresaId) {
      setAiError("Selecione um cliente para testar a IA.");
      return;
    }
    setAiLoading(true);
    try {
      await apiTestAi({ empresaId, apiKey: aiKeyInput });
      setAiMsg("Conexão OK.");
    } catch (e) {
      setAiError(String(e.message || e));
    } finally {
      setAiLoading(false);
    }
  };

  const handleAiDelete = async () => {
    setAiError("");
    setAiMsg("");
    if (!empresaId) {
      setAiError("Selecione um cliente para remover a chave.");
      return;
    }
    setAiLoading(true);
    try {
      await apiDeleteAiKey(empresaId);
      setAiEnabled(false);
      setAiHasKey(false);
      setAiKeyInput("");
      setAiMsg("Chave removida.");
    } catch (e) {
      setAiError(String(e.message || e));
    } finally {
      setAiLoading(false);
    }
  }

  const handleRelatoriosUploadLogo = async (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;
    setRelError("");
    setRelMsg("");
    if (!grupoId) {
      setRelError("Grupo nao identificado.");
      return;
    }
    setRelLoading(true);
    try {
      const cfg = await apiUploadLogoRelatorioGrupo({ grupoId, file });
      setRelCfg(cfg || null);
      setRelMsg("Logo enviada com sucesso.");
    } catch (e) {
      try {
        const safeExt = (() => {
          const mime = String(file?.type || "").toLowerCase();
          if (mime.includes("png")) return "png";
          if (mime.includes("jpeg") || mime.includes("jpg")) return "jpg";
          if (mime.includes("webp")) return "webp";
          if (mime.includes("svg")) return "svg";
          return "png";
        })();

        const logoPath = `mamute/grupos/${grupoId}/logos/relatorio_${Date.now()}.${safeExt}`;
        const storageRef = ref(storage, logoPath);
        await uploadBytes(storageRef, file, {
          contentType: file?.type || "image/png",
        });
        const logoUrl = await getDownloadURL(storageRef);

        await updateDoc(doc(db, "grupos", grupoId), {
          logoRelatorioPath: logoPath,
          updatedAt: serverTimestamp(),
        });

        setRelCfg({ logoPath, logoUrl, logoFallbackUrl: logoUrl });
        setRelMsg("Logo enviada com sucesso.");
      } catch (fallbackErr) {
        const baseError = String(e?.message || e || "Falha ao enviar logo");
        const fbError = String(fallbackErr?.message || fallbackErr || "Falha no fallback");
        setRelError(`${baseError} | ${fbError}`);
      }
    } finally {
      setRelLoading(false);
      if (event?.target) event.target.value = "";
    }
  };

  const handleSalvarEnderecoOrigem = async () => {
    if (!grupoId) {
      setEnderecoError("Usuário sem grupoId.");
      return;
    }
    
    setEnderecoError("");
    setEnderecoMsg("");
    setEnderecoLoading(true);
    
    try {
      const grupoRef = doc(db, "grupos", grupoId);
      await updateDoc(grupoRef, {
        enderecoOrigem: enderecoOrigemTemp.trim() || null,
      });
      
      setEnderecoOrigem(enderecoOrigemTemp.trim());
      setEnderecoMsg("Endereço de origem salvo com sucesso!");
    } catch (e) {
      setEnderecoError(String(e.message || e));
    } finally {
      setEnderecoLoading(false);
    }
  };

  const handleSalvarDadosEmpresaRelatorio = async () => {
    if (!grupoId) {
      setRelError("Usuário sem grupoId.");
      return;
    }

    setRelError("");
    setRelMsg("");
    setRelEmpresaSaving(true);

    const payload = {
      empresaNome: String(relEmpresaDraft.empresaNome || "").trim(),
      empresaSubtitulo: String(relEmpresaDraft.empresaSubtitulo || "").trim(),
      empresaCnpj: String(relEmpresaDraft.empresaCnpj || "").trim(),
      empresaTelefone: String(relEmpresaDraft.empresaTelefone || "").trim(),
      empresaInstagram: String(relEmpresaDraft.empresaInstagram || "").trim(),
      empresaEmail: String(relEmpresaDraft.empresaEmail || "").trim(),
      empresaSite: String(relEmpresaDraft.empresaSite || "").trim(),
      empresaEndereco: String(relEmpresaDraft.empresaEndereco || "").trim(),
    };

    try {
      const grupoRef = doc(db, "grupos", grupoId);
      await updateDoc(grupoRef, {
        "relatorios.empresaNome": payload.empresaNome,
        "relatorios.empresaSubtitulo": payload.empresaSubtitulo,
        "relatorios.empresaCnpj": payload.empresaCnpj,
        "relatorios.empresaTelefone": payload.empresaTelefone,
        "relatorios.empresaInstagram": payload.empresaInstagram,
        "relatorios.empresaEmail": payload.empresaEmail,
        "relatorios.empresaSite": payload.empresaSite,
        "relatorios.empresaEndereco": payload.empresaEndereco,
      });
      setRelEmpresa(payload);
      setRelEmpresaDraft(payload);
      setRelMsg("Dados da empresa do relatório salvos com sucesso!");
    } catch (e) {
      setRelError(String(e.message || e));
    } finally {
      setRelEmpresaSaving(false);
    }
  };

  const handleSalvarTemplatesEmailRelatorio = async () => {
    if (!grupoId) {
      setRelError("Usuário sem grupoId.");
      return;
    }

    if (relEmailFromError) {
      setRelError(relEmailFromError);
      return;
    }

    setRelError("");
    setRelMsg("");
    setRelEmailSaving(true);

    const payload = {
      emailFrom: String(relEmailDraft.emailFrom || "").trim(),
      emailTemplateBase: String(relEmailDraft.emailTemplateBase || "").trim(),
      emailTemplateInstalacao: String(relEmailDraft.emailTemplateInstalacao || "").trim(),
      emailTemplateOs: String(relEmailDraft.emailTemplateOs || "").trim(),
      emailTemplateContrato: String(relEmailDraft.emailTemplateContrato || "").trim(),
    };

    try {
      const grupoRef = doc(db, "grupos", grupoId);
      await updateDoc(grupoRef, {
        "relatorios.emailFrom": payload.emailFrom,
        "relatorios.emailTemplateBase": payload.emailTemplateBase,
        "relatorios.emailTemplateInstalacao": payload.emailTemplateInstalacao,
        "relatorios.emailTemplateOs": payload.emailTemplateOs,
        "relatorios.emailTemplateContrato": payload.emailTemplateContrato,
      });
      setRelEmail(payload);
      setRelEmailDraft(payload);
      setRelMsg("Templates de e-mail salvos com sucesso!");
    } catch (e) {
      setRelError(String(e.message || e));
    } finally {
      setRelEmailSaving(false);
    }
  };

  const handleSalvarValorKm = async () => {
    if (!grupoId) {
      setValorKmError("Usuário sem grupoId.");
      return;
    }

    const normalized = String(valorKmTemp || "").replace(",", ".").trim();
    const parsed = Number.parseFloat(normalized);
    if (Number.isNaN(parsed) || parsed < 0) {
      setValorKmError("Informe um valor válido por km.");
      return;
    }

    setValorKmError("");
    setValorKmMsg("");
    setValorKmLoading(true);

    try {
      const grupoRef = doc(db, "grupos", grupoId);
      await updateDoc(grupoRef, {
        valorKmDeslocamento: parsed,
      });

      const valueStr = parsed.toFixed(2);
      setValorKm(valueStr);
      setValorKmTemp(valueStr);
      setValorKmMsg("Valor por km salvo com sucesso!");
    } catch (e) {
      setValorKmError(String(e.message || e));
    } finally {
      setValorKmLoading(false);
    }
  };

  const handleSalvarMapsKey = async () => {
    if (!grupoId) {
      setMapsKeyError("Usuário sem grupoId.");
      return;
    }

    setMapsKeyError("");
    setMapsKeyMsg("");
    setMapsKeyLoading(true);

    try {
      const cfg = await apiSaveMapsKey({
        grupoId,
        apiKey: mapsKeyInput,
      });
      setMapsHasKey(!!cfg?.hasKey);
      setMapsKeyInput("");
      setMapsKeyMsg("API Key do Google Maps salva com sucesso!");
    } catch (e) {
      setMapsKeyError(String(e.message || e));
    } finally {
      setMapsKeyLoading(false);
    }
  };

  const handleDeleteMapsKey = async () => {
    if (!grupoId) {
      setMapsKeyError("Usuário sem grupoId.");
      return;
    }

    setMapsKeyError("");
    setMapsKeyMsg("");
    setMapsKeyLoading(true);

    try {
      await apiDeleteMapsKey(grupoId);
      setMapsHasKey(false);
      setMapsKeyMsg("API Key removida com sucesso.");
    } catch (e) {
      setMapsKeyError(String(e.message || e));
    } finally {
      setMapsKeyLoading(false);
    }
  };

  const handleSalvarWhatsAppConfig = async () => {
    if (!grupoId) {
      setWaError("Usuário sem grupoId.");
      return;
    }
    setWaError("");
    setWaMsg("");
    setWaLoading(true);

    try {
      await apiSaveWhatsAppConfig({
        grupoId,
        config: waDraft,
      });
      setWaCfg({ ...waDraft });
      setWaMsg("Configuração do WhatsApp salva com sucesso!");
    } catch (e) {
      setWaError(String(e.message || e));
    } finally {
      setWaLoading(false);
    }
  };

  const handleLimparSegredosWhatsApp = async () => {
    if (!grupoId) {
      setWaError("Usuário sem grupoId.");
      return;
    }
    setWaError("");
    setWaMsg("");
    setWaLoading(true);
    try {
      await apiClearWhatsAppSecrets(grupoId);
      const next = {
        ...waDraft,
        accessToken: "",
        phoneNumberId: "",
        verifyToken: "",
        appSecret: "",
      };
      setWaCfg(next);
      setWaDraft(next);
      setWaMsg("Credenciais do WhatsApp removidas.");
    } catch (e) {
      setWaError(String(e.message || e));
    } finally {
      setWaLoading(false);
    }
  };

  const handleTestarWhatsApp = async () => {
    const to = String(waTestTo || "").replace(/\D/g, "");
    const text = String(waTestText || "").trim();
    if (!to) {
      setWaTestError("Informe um número (com DDI), ex: 5511999999999");
      return;
    }
    if (!text) {
      setWaTestError("Informe uma mensagem");
      return;
    }
    setWaTestError("");
    setWaTestMsg("");
    setWaTestLoading(true);

    try {
      const API_BASE = (import.meta?.env?.VITE_API_BASE || "").replace(/\/$/, "");
      const url = `${API_BASE}/api/whatsapp/send`;
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ to, text }),
      });
      const raw = await res.text();
      let data = null;
      try {
        data = JSON.parse(raw);
      } catch {
        data = null;
      }
      if (!res.ok || !data?.ok) {
        throw new Error(data?.error || raw || "Falha ao enviar");
      }
      setWaTestMsg("Mensagem enviada! ✅");
    } catch (e) {
      setWaTestError(String(e.message || e));
    } finally {
      setWaTestLoading(false);
    }
  };

  // Bloqueia acesso direto via URL quando o usuário não tem permissão
  if (!podeAcessarOpcoes) {
    return (
      <LayoutDesktop titulo="Menu de Opções">
        <div className="opcoes-container">
          <div className="opcoes-section">
            <h2>Acesso restrito</h2>
            <p className="opcoes-desc">
              Você não tem permissão para acessar este menu.
            </p>
          </div>
        </div>
      </LayoutDesktop>
    );
  }

  return (
    <LayoutDesktop titulo="Menu de Opções">
      <div className="opcoes-container">
        <div className="opcoes-tabs">
          {podeAbaAparencia && (
            <button
              type="button"
              className={`opcoes-tab ${aba === "aparencia" ? "ativo" : ""}`}
              onClick={() => setAba("aparencia")}
            >
              Aparência
            </button>
          )}

          {podeAbaIA && (
            <button
              type="button"
              className={`opcoes-tab ${aba === "ia" ? "ativo" : ""}`}
              onClick={() => setAba("ia")}
            >
              IA
            </button>
          )}
          {podeAbaRelatorios && (
            <button
              type="button"
              className={`opcoes-tab ${aba === "relatorios" ? "ativo" : ""}`}
              onClick={() => setAba("relatorios")}
            >
              Relatórios
            </button>
          )}
          {podeAbaWhatsApp && (
            <button
              type="button"
              className={`opcoes-tab ${aba === "whatsapp" ? "ativo" : ""}`}
              onClick={() => setAba("whatsapp")}
            >
              WhatsApp
            </button>
          )}
          {podeAbaPermissoes && (
            <button
              type="button"
              className={`opcoes-tab ${aba === "permissoes" ? "ativo" : ""}`}
              onClick={() => setAba("permissoes")}
            >
              Permissões
            </button>
          )}
        </div>
        {aba === "aparencia" && (
        <section className="opcoes-section">
          <h2>Aparência</h2>
          <p className="opcoes-desc">Escolha o tema visual do sistema</p>

          <div className="temas-grid">
            {temas.map((t) => (
              <button
                key={t.id}
                className={`tema-card ${tema === t.id ? "ativo" : ""}`}
                onClick={() => alterarTema(t.id)}
                type="button"
              >
                <div
                  className="tema-preview"
                  style={{ backgroundColor: t.cor }}
                >
                  <div className="tema-preview-header" />
                  <div className="tema-preview-sidebar" />
                  <div className="tema-preview-content">
                    <div className="tema-preview-line" />
                    <div className="tema-preview-line short" />
                  </div>
                </div>
                <div className="tema-info">
                  <span className="tema-nome">{t.nome}</span>
                  <span className={`tema-tipo ${t.tipo}`}>
                    {t.tipo === "dark" ? "Escuro" : "Claro"}
                  </span>
                </div>
                {tema === t.id && (
                  <div className="tema-check">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </div>
                )}
              </button>
            ))}
          </div>
        </section>
        )}

        {aba === "ia" && (
        <section className="opcoes-section">
          <h2>IA (OpenAI)</h2>
          <p className="opcoes-desc">
            Configure a IA por empresa. A chave <strong>não</strong> fica exposta no navegador: o Mamute envia para o servidor.
          </p>

          {!podeConfigurarIA && (
            <div className="opcoes-alert">
              Você não tem permissão para configurar a IA. (Necessário Administrador ou Gestor)
            </div>
          )}

          {podeConfigurarIA && (
            <>
              {modoGlobal && (
                <div className="opcoes-alert">
                  Você está em modo Global. Para configurar a IA, selecione um cliente em <strong>Mudar Cliente</strong>.
                </div>
              )}

              {!empresaId && !modoGlobal && (
                <div className="opcoes-alert">
                  Selecione um cliente em <strong>Mudar Cliente</strong> para configurar a IA.
                </div>
              )}

              {!!empresaId && (
                <div className="ai-box">
                  <div className="ai-row">
                    <div className="ai-col">
                      <div className="ai-label">Empresa</div>
                      <div className="ai-value">{empresaAtual?.nome || "(sem nome)"}</div>
                    </div>
                    <div className="ai-col">
                      <div className="ai-label">Status</div>
                      <div className="ai-value">
                        {aiHasKey ? "Chave configurada" : "Sem chave"}
                        {aiEnabled ? " • IA ativa" : " • IA desativada"}
                      </div>
                    </div>
                  </div>

                  <div className="ai-row ai-form">
                    <label className="ai-field">
                      <span>Ativar IA nesta empresa</span>
                      <input
                        type="checkbox"
                        checked={aiEnabled}
                        onChange={(e) => setAiEnabled(e.target.checked)}
                        disabled={aiLoading}
                      />
                    </label>

                    <label className="ai-field">
                      <span>Modelo padrão</span>
                      <select
                        value={aiModel}
                        onChange={(e) => setAiModel(e.target.value)}
                        disabled={aiLoading}
                      >
                        <option value="gpt-5-nano">GPT-5 Nano (triagem)</option>
                        <option value="gpt-5-mini">GPT-5 Mini (padrão)</option>
                        <option value="gpt-5.2">GPT-5.2 (casos difíceis)</option>
                      </select>
                    </label>

                    <label className="ai-field ai-field-wide">
                      <span>API Key (OpenAI)</span>
                      <input
                        type="password"
                        placeholder={aiHasKey ? "•••••••••• (já configurada)" : "Cole sua chave aqui"}
                        value={aiKeyInput}
                        onChange={(e) => setAiKeyInput(e.target.value)}
                        disabled={aiLoading}
                      />
                      <small>
                        Dica: você só precisa colar a chave novamente se quiser trocar.
                      </small>
                    </label>
                  </div>

                  <div className="ai-actions">
                    <button type="button" className="btn" onClick={handleAiTest} disabled={aiLoading}>
                      Testar conexão
                    </button>
                    <button type="button" className="btn primary" onClick={handleAiSave} disabled={aiLoading}>
                      Salvar
                    </button>
                    <button type="button" className="btn danger" onClick={handleAiDelete} disabled={aiLoading || !aiHasKey}>
                      Remover chave
                    </button>
                  </div>

                  {aiError && <div className="opcoes-alert error">{aiError}</div>}
                  {aiMsg && <div className="opcoes-alert ok">{aiMsg}</div>}

                  <div className="ai-help">
                    <h3>Como gerar a API Key</h3>
                    <ol>
                      <li>Acesse a plataforma da OpenAI e crie uma API key.</li>
                      <li>Copie a chave (ela aparece uma única vez).</li>
                      <li>Cole aqui e clique em <strong>Testar conexão</strong>.</li>
                      <li>Clique em <strong>Salvar</strong>.</li>
                    </ol>

                    <h3>O que a IA pode fazer no Mamute</h3>
                    <ul>
                      <li>Resumir e padronizar texto de OS (ação/correção/causa).</li>
                      <li>Sugerir checklists de liberação e itens de preventiva.</li>
                      <li>Tirar dúvidas técnicas e orientar procedimentos.</li>
                      <li>Interpretar indicadores e apontar tendências em tabelas.</li>
                    </ul>
                  </div>
                </div>
              )}
            </>
          )}
        </section>
        )}

        {aba === "relatorios" && (
        <section className="opcoes-section">
          <h2>Relatórios</h2>
          <p className="opcoes-desc">Configure a logo e preferências visuais dos relatórios.</p>

          {!podeConfigurarRelatorios && (
            <div className="opcoes-alert">
              Você não tem permissão para configurar relatórios. (Necessário Administrador ou Gestor)
            </div>
          )}

          {podeConfigurarRelatorios && (
            <>
              {!!grupoId && (
                <div className="rel-box">
                  <div className="rel-row">
                    <div className="rel-col">
                      <div className="rel-label">Grupo</div>
                      <div className="rel-value">{usuario?.grupoNome || usuario?.grupoId || "(sem nome)"}</div>
                    </div>
                    <div className="rel-col">
                      <div className="rel-label">Logo atual</div>
                      {relCfg?.logoUrl ? (
                        <div className="rel-preview">
                          <img src={relCfg.logoUrl} alt="Logo do relatório" />
                        </div>
                      ) : (
                        <div className="rel-empty">Nenhuma logo enviada</div>
                      )}
                    </div>
                  </div>

                  <div className="rel-row rel-form">
                    <label className="rel-field rel-field-wide">
                      <span>Enviar nova logo (PNG/JPG)</span>
                      <input
                        type="file"
                        accept="image/png, image/jpeg"
                        onChange={handleRelatoriosUploadLogo}
                        disabled={relLoading}
                      />
                      <small>Recomendado: fundo transparente e largura ate 1024px.</small>
                    </label>
                  </div>

                  {relLoading && <div className="opcoes-alert">Carregando…</div>}
                  {relError && <div className="opcoes-alert error">{relError}</div>}
                  {relMsg && <div className="opcoes-alert ok">{relMsg}</div>}
                </div>
              )}

              {!!grupoId && (
                <div className="rel-box" style={{ marginTop: 24 }}>
                  <h3 style={{ marginBottom: 12, fontSize: 16, fontWeight: 600 }}>
                    Dados da Empresa (Rodapé/Cabeçalho do relatório)
                  </h3>

                  <div className="rel-row rel-form">
                    <label className="rel-field">
                      <span>Nome da empresa</span>
                      <input
                        type="text"
                        value={relEmpresaDraft.empresaNome}
                        onChange={(e) => setRelEmpresaDraft((prev) => ({ ...prev, empresaNome: e.target.value }))}
                        disabled={relEmpresaSaving}
                      />
                    </label>
                    <label className="rel-field">
                      <span>Subtítulo</span>
                      <input
                        type="text"
                        value={relEmpresaDraft.empresaSubtitulo}
                        onChange={(e) => setRelEmpresaDraft((prev) => ({ ...prev, empresaSubtitulo: e.target.value }))}
                        disabled={relEmpresaSaving}
                      />
                    </label>
                  </div>

                  <div className="rel-row rel-form">
                    <label className="rel-field">
                      <span>CNPJ</span>
                      <input
                        type="text"
                        value={relEmpresaDraft.empresaCnpj}
                        onChange={(e) => setRelEmpresaDraft((prev) => ({ ...prev, empresaCnpj: e.target.value }))}
                        disabled={relEmpresaSaving}
                      />
                    </label>
                    <label className="rel-field">
                      <span>Telefone</span>
                      <input
                        type="text"
                        value={relEmpresaDraft.empresaTelefone}
                        onChange={(e) => setRelEmpresaDraft((prev) => ({ ...prev, empresaTelefone: e.target.value }))}
                        disabled={relEmpresaSaving}
                      />
                    </label>
                  </div>

                  <div className="rel-row rel-form">
                    <label className="rel-field">
                      <span>Instagram</span>
                      <input
                        type="text"
                        value={relEmpresaDraft.empresaInstagram}
                        onChange={(e) => setRelEmpresaDraft((prev) => ({ ...prev, empresaInstagram: e.target.value }))}
                        disabled={relEmpresaSaving}
                      />
                    </label>
                    <label className="rel-field">
                      <span>E-mail</span>
                      <input
                        type="text"
                        value={relEmpresaDraft.empresaEmail}
                        onChange={(e) => setRelEmpresaDraft((prev) => ({ ...prev, empresaEmail: e.target.value }))}
                        disabled={relEmpresaSaving}
                      />
                    </label>
                  </div>

                  <div className="rel-row rel-form">
                    <label className="rel-field">
                      <span>Site</span>
                      <input
                        type="text"
                        value={relEmpresaDraft.empresaSite}
                        onChange={(e) => setRelEmpresaDraft((prev) => ({ ...prev, empresaSite: e.target.value }))}
                        disabled={relEmpresaSaving}
                      />
                    </label>
                    <label className="rel-field">
                      <span>Endereço</span>
                      <input
                        type="text"
                        value={relEmpresaDraft.empresaEndereco}
                        onChange={(e) => setRelEmpresaDraft((prev) => ({ ...prev, empresaEndereco: e.target.value }))}
                        disabled={relEmpresaSaving}
                      />
                    </label>
                  </div>

                  <div className="rel-actions" style={{ display: "flex", gap: 8, marginTop: 12 }}>
                    <button
                      type="button"
                      className="btn primary"
                      onClick={handleSalvarDadosEmpresaRelatorio}
                      disabled={relEmpresaSaving}
                    >
                      {relEmpresaSaving ? "Salvando..." : "Salvar dados da empresa"}
                    </button>
                    <button
                      type="button"
                      className="btn"
                      onClick={() => setRelEmpresaDraft(relEmpresa)}
                      disabled={relEmpresaSaving}
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              )}

              {!!grupoId && (
                <div className="rel-box" style={{ marginTop: 24 }}>
                  <h3 style={{ marginBottom: 12, fontSize: 16, fontWeight: 600 }}>
                    Templates de E-mail (Propostas)
                  </h3>
                  <p style={{ fontSize: 13, marginBottom: 12, color: "var(--text-secondary, #666)" }}>
                    Use placeholders como: {"{{clienteNome}}"}, {"{{academiaNome}}"}, {"{{tipoDocumento}}"}, {"{{valorTotal}}"}, {"{{contratoTipo}}"}, {"{{dataInicial}}"}.
                  </p>

                  <details style={{ marginBottom: 12 }}>
                    <summary style={{ cursor: "pointer", fontWeight: 600 }}>
                      Tutorial rápido • Como configurar envio de e-mail pelo APP
                    </summary>
                    <ol style={{ marginTop: 8, paddingLeft: 18 }}>
                      <li>
                        No provedor de e-mail (Resend), verifique seu domínio e configure os
                        registros DNS solicitados (SPF/DKIM).
                      </li>
                      <li>
                        Nesta tela, preencha <strong>E-mail remetente</strong> com um e-mail do
                        domínio verificado (ex.: Nome &lt;comercial@seudominio.com&gt;).
                      </li>
                      <li>
                        Ajuste as mensagens (base e específicas por tipo) e clique em
                        <strong> Salvar templates de e-mail</strong>.
                      </li>
                      <li>
                        Faça um envio de teste por proposta para validar entrega.
                      </li>
                    </ol>
                    <p style={{ fontSize: 13, color: "var(--text-secondary, #666)" }}>
                      Se aparecer erro de remetente inválido ou envio não autorizado, revise o
                      domínio/e-mail no provedor antes de tentar novamente.
                    </p>
                  </details>

                  <div className="rel-row rel-form">
                    <label className="rel-field rel-field-wide">
                      <span>E-mail remetente</span>
                      <input
                        type="text"
                        value={relEmailDraft.emailFrom}
                        onChange={(e) => setRelEmailDraft((prev) => ({ ...prev, emailFrom: e.target.value }))}
                        placeholder="Ex: Comercial Mamute <comercial@seudominio.com>"
                        disabled={relEmailSaving}
                      />
                      <small>
                        Se vazio, o sistema usa a variável de ambiente EMAIL_FROM no servidor.
                      </small>
                      {relEmailFromError && (
                        <small style={{ color: "#ef4444", display: "block", marginTop: 4 }}>
                          {relEmailFromError}
                        </small>
                      )}
                    </label>
                  </div>

                  <div className="rel-row rel-form">
                    <label className="rel-field rel-field-wide">
                      <span>Mensagem base</span>
                      <textarea
                        rows={6}
                        value={relEmailDraft.emailTemplateBase}
                        onChange={(e) => setRelEmailDraft((prev) => ({ ...prev, emailTemplateBase: e.target.value }))}
                        disabled={relEmailSaving}
                      />
                    </label>
                  </div>

                  <div className="rel-row rel-form">
                    <label className="rel-field rel-field-wide">
                      <span>Mensagem específica • Instalação</span>
                      <textarea
                        rows={5}
                        value={relEmailDraft.emailTemplateInstalacao}
                        onChange={(e) => setRelEmailDraft((prev) => ({ ...prev, emailTemplateInstalacao: e.target.value }))}
                        disabled={relEmailSaving}
                      />
                    </label>
                  </div>

                  <div className="rel-row rel-form">
                    <label className="rel-field rel-field-wide">
                      <span>Mensagem específica • Ordem de Serviço</span>
                      <textarea
                        rows={5}
                        value={relEmailDraft.emailTemplateOs}
                        onChange={(e) => setRelEmailDraft((prev) => ({ ...prev, emailTemplateOs: e.target.value }))}
                        disabled={relEmailSaving}
                      />
                    </label>
                  </div>

                  <div className="rel-row rel-form">
                    <label className="rel-field rel-field-wide">
                      <span>Mensagem específica • Contrato</span>
                      <textarea
                        rows={5}
                        value={relEmailDraft.emailTemplateContrato}
                        onChange={(e) => setRelEmailDraft((prev) => ({ ...prev, emailTemplateContrato: e.target.value }))}
                        disabled={relEmailSaving}
                      />
                    </label>
                  </div>

                  <div className="rel-actions" style={{ display: "flex", gap: 8, marginTop: 12 }}>
                    <button
                      type="button"
                      className="btn primary"
                      onClick={handleSalvarTemplatesEmailRelatorio}
                      disabled={relEmailSaving || !!relEmailFromError}
                    >
                      {relEmailSaving ? "Salvando..." : "Salvar templates de e-mail"}
                    </button>
                    <button
                      type="button"
                      className="btn"
                      onClick={() => setRelEmailDraft(relEmail)}
                      disabled={relEmailSaving}
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              )}

              {/* Configuração do endereço de origem para Google Maps */}
              {!!grupoId && (
                <div className="rel-box" style={{ marginTop: 24 }}>
                  <h3 style={{ marginBottom: 12, fontSize: 16, fontWeight: 600 }}>
                    Endereço de Origem (Google Maps)
                  </h3>
                  <p style={{ fontSize: 13, marginBottom: 16, color: "var(--text-secondary, #666)" }}>
                    Configure o endereço da sua empresa para cálculo automático de distância ao agendar sessões.
                  </p>

                  <div className="rel-row">
                    <div className="rel-col">
                      <div className="rel-label">Endereço atual</div>
                      <div className="rel-value">
                        {enderecoOrigem || "(Não configurado)"}
                      </div>
                    </div>
                    <div className="rel-col">
                      <div className="rel-label">API Key (status)</div>
                      <div className="rel-value">
                        {mapsHasKey ? "Chave configurada" : "Sem chave"}
                      </div>
                    </div>
                  </div>

                  <div className="rel-row rel-form">
                    <label className="rel-field rel-field-wide">
                      <span>Endereço completo da empresa</span>
                      <textarea
                        rows={3}
                        value={enderecoOrigemTemp}
                        onChange={(e) => setEnderecoOrigemTemp(e.target.value)}
                        placeholder="Ex: Rua Exemplo, 123 - Bairro, Cidade - UF, CEP"
                        disabled={enderecoLoading}
                        style={{ fontFamily: "inherit", resize: "vertical" }}
                      />
                      <small>
                        Este endereço será usado como ponto de partida para calcular distâncias até as academias/clientes.
                        Use um endereço completo para melhor precisão.
                      </small>
                    </label>
                  </div>

                  <div className="rel-actions" style={{ display: "flex", gap: 8, marginTop: 12 }}>
                    <button
                      type="button"
                      className="btn primary"
                      onClick={handleSalvarEnderecoOrigem}
                      disabled={enderecoLoading || enderecoOrigemTemp === enderecoOrigem}
                    >
                      {enderecoLoading ? "Salvando..." : "Salvar Endereço"}
                    </button>
                    <button
                      type="button"
                      className="btn"
                      onClick={() => setEnderecoOrigemTemp(enderecoOrigem)}
                      disabled={enderecoLoading}
                    >
                      Cancelar
                    </button>
                  </div>

                  {enderecoError && <div className="opcoes-alert error" style={{ marginTop: 12 }}>{enderecoError}</div>}
                  {enderecoMsg && <div className="opcoes-alert ok" style={{ marginTop: 12 }}>{enderecoMsg}</div>}

                  <div className="rel-row rel-form" style={{ marginTop: 16 }}>
                    <label className="rel-field">
                      <span>Valor por km (R$)</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={valorKmTemp}
                        onChange={(e) => setValorKmTemp(e.target.value)}
                        disabled={valorKmLoading}
                      />
                      <small>
                        Este valor sera usado para calcular o deslocamento automaticamente (km x valor).
                      </small>
                    </label>
                  </div>

                  <div className="rel-actions" style={{ display: "flex", gap: 8, marginTop: 12 }}>
                    <button
                      type="button"
                      className="btn primary"
                      onClick={handleSalvarValorKm}
                      disabled={valorKmLoading || valorKmTemp === valorKm}
                    >
                      {valorKmLoading ? "Salvando..." : "Salvar valor por km"}
                    </button>
                    <button
                      type="button"
                      className="btn"
                      onClick={() => setValorKmTemp(valorKm)}
                      disabled={valorKmLoading}
                    >
                      Cancelar
                    </button>
                  </div>

                  {valorKmError && <div className="opcoes-alert error" style={{ marginTop: 12 }}>{valorKmError}</div>}
                  {valorKmMsg && <div className="opcoes-alert ok" style={{ marginTop: 12 }}>{valorKmMsg}</div>}

                  <div className="rel-row rel-form" style={{ marginTop: 16 }}>
                    <label className="rel-field rel-field-wide">
                      <span>API Key do Google Maps</span>
                      <input
                        type="password"
                        placeholder={mapsHasKey ? "•••••••••• (já configurada)" : "Cole sua chave aqui"}
                        value={mapsKeyInput}
                        onChange={(e) => setMapsKeyInput(e.target.value)}
                        disabled={mapsKeyLoading}
                      />
                      <small>
                        A chave é salva de forma segura no servidor e usada pelo backend para calcular distância e autocomplete.
                      </small>
                    </label>
                  </div>

                  <div className="rel-actions" style={{ display: "flex", gap: 8, marginTop: 12 }}>
                    <button
                      type="button"
                      className="btn primary"
                      onClick={handleSalvarMapsKey}
                      disabled={mapsKeyLoading || !mapsKeyInput.trim()}
                    >
                      {mapsKeyLoading ? "Salvando..." : "Salvar API Key"}
                    </button>
                    <button
                      type="button"
                      className="btn danger"
                      onClick={handleDeleteMapsKey}
                      disabled={mapsKeyLoading || !mapsHasKey}
                    >
                      Remover chave
                    </button>
                  </div>

                  {mapsKeyError && <div className="opcoes-alert error" style={{ marginTop: 12 }}>{mapsKeyError}</div>}
                  {mapsKeyMsg && <div className="opcoes-alert ok" style={{ marginTop: 12 }}>{mapsKeyMsg}</div>}
                </div>
              )}
            </>
          )}
        </section>
        )}

        {/* ===================== WhatsApp ===================== */}
        {aba === "whatsapp" && (
          <section className="opcoes-section">
            <h2>WhatsApp</h2>
            <p className="opcoes-desc">
              Configure a integração do WhatsApp (Cloud API) e os textos do bot.
            </p>

            {!podeConfigurarWhatsApp && (
              <div className="opcoes-alert">
                Você não tem permissão para configurar WhatsApp. (Necessário Gestor)
              </div>
            )}

            {podeConfigurarWhatsApp && (
              <>
                <div className="rel-box">
                  <h3 style={{ marginBottom: 12, fontSize: 16, fontWeight: 600 }}>
                    Conexão
                  </h3>

                  <div className="rel-row">
                    <div className="rel-col">
                      <div className="rel-label">Webhook URL</div>
                      <div className="rel-value" style={{ wordBreak: "break-all" }}>
                        {whatsappWebhookUrl}
                      </div>
                    </div>
                    <div className="rel-col">
                      <div className="rel-label">Grupo</div>
                      <div className="rel-value">{grupoId || "(sem grupo)"}</div>
                    </div>
                  </div>

                  <p style={{ fontSize: 13, color: "var(--text-secondary, #666)", marginTop: 10 }}>
                    Dica: se quiser que o servidor leia as credenciais do Firestore, defina <b>WHATSAPP_GRUPO_ID</b> no .env do backend.
                  </p>
                </div>

                <div className="rel-box" style={{ marginTop: 16 }}>
                  <h3 style={{ marginBottom: 12, fontSize: 16, fontWeight: 600 }}>
                    Configuração
                  </h3>

                  <div className="rel-row rel-form" style={{ gap: 16 }}>
                    <label className="rel-field">
                      <span>
                        <input
                          type="checkbox"
                          checked={!!waDraft.enabled}
                          onChange={(e) => setWaDraft((p) => ({ ...p, enabled: e.target.checked }))}
                          disabled={waLoading}
                          style={{ marginRight: 8 }}
                        />
                        Integração ativa
                      </span>
                      <small>Se desativar, o sistema não deve disparar mensagens automáticas.</small>
                    </label>

                    <label className="rel-field">
                      <span>
                        <input
                          type="checkbox"
                          checked={waDraft.botEnabled !== false}
                          onChange={(e) => setWaDraft((p) => ({ ...p, botEnabled: e.target.checked }))}
                          disabled={waLoading}
                          style={{ marginRight: 8 }}
                        />
                        Bot automático
                      </span>
                      <small>Controla as respostas automáticas do webhook.</small>
                    </label>
                  </div>

                  <div className="rel-row rel-form">
                    <label className="rel-field">
                      <span>Empresa padrão (empresaId)</span>
                      <input
                        type="text"
                        value={waDraft.empresaIdDefault || ""}
                        onChange={(e) => setWaDraft((p) => ({ ...p, empresaIdDefault: e.target.value }))}
                        disabled={waLoading}
                        placeholder="Opcional (ex: id da empresa)"
                      />
                      <small>
                        Usado para registrar conversas/triagens quando o webhook não consegue identificar a empresa.
                      </small>
                    </label>
                  </div>
                </div>

                <div className="rel-box" style={{ marginTop: 16 }}>
                  <h3 style={{ marginBottom: 12, fontSize: 16, fontWeight: 600 }}>
                    Credenciais (Cloud API)
                  </h3>

                  <div className="rel-row rel-form" style={{ gap: 16 }}>
                    <label className="rel-field">
                      <span>Phone Number ID</span>
                      <input
                        type="text"
                        value={waDraft.phoneNumberId || ""}
                        onChange={(e) => setWaDraft((p) => ({ ...p, phoneNumberId: e.target.value }))}
                        disabled={waLoading}
                        placeholder="Ex: 1234567890"
                      />
                    </label>

                    <label className="rel-field">
                      <span>Verify Token</span>
                      <input
                        type="password"
                        value={waDraft.verifyToken || ""}
                        onChange={(e) => setWaDraft((p) => ({ ...p, verifyToken: e.target.value }))}
                        disabled={waLoading}
                        placeholder="Token de verificação do webhook"
                      />
                    </label>
                  </div>

                  <div className="rel-row rel-form" style={{ gap: 16 }}>
                    <label className="rel-field rel-field-wide">
                      <span>Access Token</span>
                      <input
                        type="password"
                        value={waDraft.accessToken || ""}
                        onChange={(e) => setWaDraft((p) => ({ ...p, accessToken: e.target.value }))}
                        disabled={waLoading}
                        placeholder="Token de acesso da Cloud API"
                      />
                      <small>
                        Se você preferir, pode manter no .env do servidor (WHATSAPP_ACCESS_TOKEN). Se ambos existirem, o servidor usa o .env.
                      </small>
                    </label>
                  </div>

                  <div className="rel-row rel-form">
                    <label className="rel-field rel-field-wide">
                      <span>App Secret (opcional)</span>
                      <input
                        type="password"
                        value={waDraft.appSecret || ""}
                        onChange={(e) => setWaDraft((p) => ({ ...p, appSecret: e.target.value }))}
                        disabled={waLoading}
                        placeholder="Usado para validar assinatura do webhook"
                      />
                    </label>
                  </div>

                  <div className="rel-actions" style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
                    <button
                      type="button"
                      className="btn primary"
                      onClick={handleSalvarWhatsAppConfig}
                      disabled={waLoading}
                    >
                      {waLoading ? "Salvando..." : "Salvar configuração"}
                    </button>
                    <button
                      type="button"
                      className="btn"
                      onClick={() => setWaDraft({ ...waCfg })}
                      disabled={waLoading}
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      className="btn danger"
                      onClick={handleLimparSegredosWhatsApp}
                      disabled={waLoading}
                    >
                      Remover credenciais
                    </button>
                  </div>

                  {waError && <div className="opcoes-alert error" style={{ marginTop: 12 }}>{waError}</div>}
                  {waMsg && <div className="opcoes-alert ok" style={{ marginTop: 12 }}>{waMsg}</div>}
                </div>

                <div className="rel-box" style={{ marginTop: 16 }}>
                  <h3 style={{ marginBottom: 12, fontSize: 16, fontWeight: 600 }}>
                    Textos do bot
                  </h3>

                  <div className="rel-row rel-form">
                    <label className="rel-field rel-field-wide">
                      <span>Mensagem de boas-vindas / menu</span>
                      <textarea
                        rows={6}
                        value={waDraft.welcomeMessage || ""}
                        onChange={(e) => setWaDraft((p) => ({ ...p, welcomeMessage: e.target.value }))}
                        disabled={waLoading}
                        style={{ fontFamily: "inherit", resize: "vertical" }}
                      />
                    </label>
                  </div>

                  <div className="rel-row rel-form">
                    <label className="rel-field rel-field-wide">
                      <span>Template • Confirmação de visita</span>
                      <textarea
                        rows={3}
                        value={waDraft?.templates?.confirmacaoVisita || ""}
                        onChange={(e) =>
                          setWaDraft((p) => ({
                            ...p,
                            templates: { ...(p.templates || {}), confirmacaoVisita: e.target.value },
                          }))
                        }
                        disabled={waLoading}
                        style={{ fontFamily: "inherit", resize: "vertical" }}
                      />
                      <small>Variáveis: {"{{dataHora}}"}, {"{{unidade}}"}</small>
                    </label>
                  </div>

                  <div className="rel-row rel-form">
                    <label className="rel-field rel-field-wide">
                      <span>Template • Lembrete</span>
                      <textarea
                        rows={2}
                        value={waDraft?.templates?.lembreteVisita || ""}
                        onChange={(e) =>
                          setWaDraft((p) => ({
                            ...p,
                            templates: { ...(p.templates || {}), lembreteVisita: e.target.value },
                          }))
                        }
                        disabled={waLoading}
                        style={{ fontFamily: "inherit", resize: "vertical" }}
                      />
                      <small>Variáveis: {"{{dataHora}}"}, {"{{unidade}}"}</small>
                    </label>
                  </div>

                  <div className="rel-row rel-form">
                    <label className="rel-field rel-field-wide">
                      <span>Template • Técnico a caminho</span>
                      <textarea
                        rows={2}
                        value={waDraft?.templates?.tecnicoACaminho || ""}
                        onChange={(e) =>
                          setWaDraft((p) => ({
                            ...p,
                            templates: { ...(p.templates || {}), tecnicoACaminho: e.target.value },
                          }))
                        }
                        disabled={waLoading}
                        style={{ fontFamily: "inherit", resize: "vertical" }}
                      />
                      <small>Variável: {"{{eta}}"}</small>
                    </label>
                  </div>

                  <div className="rel-row rel-form">
                    <label className="rel-field rel-field-wide">
                      <span>Template • Finalizado</span>
                      <textarea
                        rows={2}
                        value={waDraft?.templates?.finalizado || ""}
                        onChange={(e) =>
                          setWaDraft((p) => ({
                            ...p,
                            templates: { ...(p.templates || {}), finalizado: e.target.value },
                          }))
                        }
                        disabled={waLoading}
                        style={{ fontFamily: "inherit", resize: "vertical" }}
                      />
                    </label>
                  </div>

                  <div className="rel-actions" style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
                    <button
                      type="button"
                      className="btn primary"
                      onClick={handleSalvarWhatsAppConfig}
                      disabled={waLoading}
                    >
                      {waLoading ? "Salvando..." : "Salvar textos"}
                    </button>
                    <button
                      type="button"
                      className="btn"
                      onClick={() => setWaDraft({ ...waCfg })}
                      disabled={waLoading}
                    >
                      Cancelar
                    </button>
                  </div>
                </div>

                <div className="rel-box" style={{ marginTop: 16 }}>
                  <h3 style={{ marginBottom: 12, fontSize: 16, fontWeight: 600 }}>
                    Teste rápido
                  </h3>
                  <p style={{ fontSize: 13, marginBottom: 12, color: "var(--text-secondary, #666)" }}>
                    Envia uma mensagem pelo endpoint do servidor <code>/api/whatsapp/send</code>.
                  </p>

                  <div className="rel-row rel-form" style={{ gap: 16 }}>
                    <label className="rel-field">
                      <span>Número (com DDI)</span>
                      <input
                        type="text"
                        value={waTestTo}
                        onChange={(e) => setWaTestTo(e.target.value)}
                        disabled={waTestLoading}
                        placeholder="Ex: 5511999999999"
                      />
                    </label>
                    <label className="rel-field rel-field-wide">
                      <span>Mensagem</span>
                      <input
                        type="text"
                        value={waTestText}
                        onChange={(e) => setWaTestText(e.target.value)}
                        disabled={waTestLoading}
                      />
                    </label>
                  </div>

                  <div className="rel-actions" style={{ display: "flex", gap: 8, marginTop: 12 }}>
                    <button
                      type="button"
                      className="btn primary"
                      onClick={handleTestarWhatsApp}
                      disabled={waTestLoading}
                    >
                      {waTestLoading ? "Enviando..." : "Enviar teste"}
                    </button>
                  </div>

                  {waTestError && <div className="opcoes-alert error" style={{ marginTop: 12 }}>{waTestError}</div>}
                  {waTestMsg && <div className="opcoes-alert ok" style={{ marginTop: 12 }}>{waTestMsg}</div>}
                </div>
              </>
            )}
          </section>
        )}

        {/* ===================== Permissões (Avançado) ===================== */}
        {aba === "permissoes" && (
          <section className="opcoes-section">
            <h2>Permissões (Avançado)</h2>
            <p className="opcoes-desc">
              Ajuste o nível mínimo e o escopo por nível para cada ação. Você pode salvar por <b>grupo</b> (padrão) ou para um <b>usuário específico</b> (exceção).
            </p>

            {!podeGerenciarPermissoes && (
              <div className="opcoes-alert">
                Você não tem permissão para gerenciar permissões. (Necessário Gestor)
              </div>
            )}

            {podeGerenciarPermissoes && (
              <>

                <div className="perm-actions" style={{ marginBottom: 10 }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <label className="perm-cell-mono">Aplicar para:</label>
                    <select
                      value={permAlvo}
                      onChange={(e) => setPermAlvo(e.target.value)}
                      className="perm-level-select"
                    >
                      <option value="GRUPO">Grupo</option>
                      <option value="USUARIO">Usuário específico</option>
                    </select>

                    {permAlvo === "USUARIO" && (
                      <>
                        <label className="perm-cell-mono">Usuário:</label>
                        <select
                          value={permUserId}
                          onChange={(e) => setPermUserId(e.target.value)}
                          className="perm-level-select"
                        >
                          <option value="">Selecione…</option>
                          {permUsers.map((u) => (
                            <option key={u.id} value={u.id}>
                              {u.nome} {u.nivel != null ? `(${u.nivel})` : ""}
                            </option>
                          ))}
                        </select>
                      </>
                    )}
                  </div>
                </div>


            {!permDocExists && (
              <div className="opcoes-alert">
                Este grupo ainda não tem o documento <b>config/permissoes</b> no Firebase. Isso é normal no primeiro uso.
                Use o botão <b>Inicializar</b> para criar o documento e passar a ter auditoria/edição centralizada.
              </div>
            )}

            <div className="perm-toolbar">
              <div className="perm-meta">
                <div><b>Grupo:</b> {usuario?.grupoNome || "-"} <span className="perm-muted">({grupoId || "sem grupoId"})</span></div>
              </div>
              <div className="perm-actions">
                <input
                  type="text"
                  placeholder="Buscar (ex.: cadastros usuários altera)…"
                  value={permSearch}
                  onChange={(e) => setPermSearch(e.target.value)}
                  className="perm-search"
                />
                <button type="button" className="btn" onClick={handleAiTest} disabled={aiLoading}>
                  {permAlvo === "GRUPO" ? "Restaurar padrão" : "Limpar exceção"}
                </button>
                {permAlvo === "GRUPO" && (
                  <button type="button" className="btn" onClick={handlePermInit} disabled={permLoading || permDocExists}>
                    Inicializar
                  </button>
                )}
                

                <button type="button" className="btn primary" onClick={handlePermSave} disabled={permLoading}>
                  Salvar
                </button>
              </div>
            </div>

            {permLoading && <div className="opcoes-alert">Carregando…</div>}
            {permError && <div className="opcoes-alert error">{permError}</div>}
            {permMsg && <div className="opcoes-alert ok">{permMsg}</div>}

            <div className="perm-dual-toolbar">
              <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                <label className="perm-cell-mono">Simular nível:</label>
                <select
                  value={permNivelTeste}
                  onChange={(e) => setPermNivelTeste(Number(e.target.value))}
                  className="perm-level-select"
                >
                  {NIVEIS_UI.map(({ n, label }) => (
                    <option key={n} value={n}>
                      {label} ({n})
                    </option>
                  ))}
                </select>
                <span className="perm-muted">Esquerda: não acessa • Direita: acessa</span>
              </div>
            </div>

            <div className="perm-dual-wrap">
              <div className="perm-box">
                <div className="perm-box-head">
                  <div><b>Sem acesso</b> <span className="perm-count">({permLeftRows.length})</span></div>
                  <small className="perm-muted">Clique para selecionar, duplo clique para liberar</small>
                </div>
                <div className="perm-list">
                  {permLeftRows.map((r) => {
                    const ed = permEdit[r.id] || effectiveMap[r.id] || { nivelMinimo: r.nivelMinimo, escopo: r.escopo || {} };
                    const origem = permOrigin(r.id);
                    return (
                      <div
                        key={r.id}
                        className={`perm-item ${permFocusId === r.id ? "focus" : ""}`}
                        onClick={() => setPermFocusId(r.id)}
                        onDoubleClick={() => grantPermIds([r.id])}
                        role="button"
                        tabIndex={0}
                      >
                        <label className="perm-item-left" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={!!permPickLeft[r.id]}
                            onChange={() => setPermPickLeft((p) => ({ ...p, [r.id]: !p[r.id] }))}
                          />
                          <span className="perm-item-title">{r.modulo} / {r.recurso} / {r.acao}</span>
                        </label>
                        <div className="perm-item-right">
                          <span className={`perm-badge ${origem === "USUÁRIO" ? "user" : origem === "GRUPO" ? "group" : "default"}`}>{origem}</span>
                          <span className="perm-mini">min: {Number(ed.nivelMinimo ?? 0)}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="perm-move">
                <button
                  type="button"
                  className="btn"
                  onClick={grantPicked}
                  disabled={permLoading || (permAlvo === "USUARIO" && !permUserId)}
                  title="Liberar selecionadas"
                >
                  ▶
                </button>
                <button
                  type="button"
                  className="btn"
                  onClick={grantAllFiltered}
                  disabled={permLoading || (permAlvo === "USUARIO" && !permUserId)}
                  title="Liberar todas"
                >
                  ▶▶
                </button>
                <button
                  type="button"
                  className="btn"
                  onClick={revokePicked}
                  disabled={permLoading || (permAlvo === "USUARIO" && !permUserId)}
                  title="Remover selecionadas"
                >
                  ◀
                </button>
                <button
                  type="button"
                  className="btn"
                  onClick={revokeAllFiltered}
                  disabled={permLoading || (permAlvo === "USUARIO" && !permUserId)}
                  title="Remover todas"
                >
                  ◀◀
                </button>
              </div>

              <div className="perm-box">
                <div className="perm-box-head">
                  <div><b>Acessa</b> <span className="perm-count">({permRightRows.length})</span></div>
                  <small className="perm-muted">Clique para selecionar, duplo clique para remover</small>
                </div>
                <div className="perm-list">
                  {permRightRows.map((r) => {
                    const ed = permEdit[r.id] || effectiveMap[r.id] || { nivelMinimo: r.nivelMinimo, escopo: r.escopo || {} };
                    const origem = permOrigin(r.id);
                    return (
                      <div
                        key={r.id}
                        className={`perm-item ${permFocusId === r.id ? "focus" : ""}`}
                        onClick={() => setPermFocusId(r.id)}
                        onDoubleClick={() => revokePermIds([r.id])}
                        role="button"
                        tabIndex={0}
                      >
                        <label className="perm-item-left" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={!!permPickRight[r.id]}
                            onChange={() => setPermPickRight((p) => ({ ...p, [r.id]: !p[r.id] }))}
                          />
                          <span className="perm-item-title">{r.modulo} / {r.recurso} / {r.acao}</span>
                        </label>
                        <div className="perm-item-right">
                          <span className={`perm-badge ${origem === "USUÁRIO" ? "user" : origem === "GRUPO" ? "group" : "default"}`}>{origem}</span>
                          <span className="perm-mini">min: {Number(ed.nivelMinimo ?? 0)}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {permFocusId && rowById[permFocusId] && (
              <div className="perm-editor">
                <div className="perm-editor-head">
                  <b>Editar permissão</b>
                  <span className="perm-muted">({rowById[permFocusId].id})</span>
                </div>

                <div className="perm-editor-grid">
                  <div>
                    <div className="perm-muted">Chave</div>
                    <div className="perm-cell-mono">{rowById[permFocusId].modulo} / {rowById[permFocusId].recurso} / {rowById[permFocusId].acao}</div>
                  </div>

                  <div>
                    <div className="perm-muted">Nível mínimo</div>
                    <select
                      value={Number((permEdit[permFocusId]?.nivelMinimo ?? effectiveMap[permFocusId]?.nivelMinimo ?? 0))}
                      onChange={(e) => setPermEdit((prev) => ({
                        ...prev,
                        [permFocusId]: {
                          ...(prev[permFocusId] || { escopo: (effectiveMap[permFocusId]?.escopo || {}) }),
                          nivelMinimo: Number(e.target.value),
                        },
                      }))}
                      className="perm-level-select"
                    >
                      {NIVEIS_UI.map(({ n, label }) => (
                        <option key={n} value={n}>
                          {label} ({n})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="perm-editor-scope">
                  <div className="perm-muted">Escopo por nível (clique para alternar ✓ → P → X)</div>
                  <div className="perm-scope-row">
                    {NIVEIS_UI.map(({ n: lvl, label }) => {
                      const ed = permEdit[permFocusId] || effectiveMap[permFocusId] || { nivelMinimo: 0, escopo: {} };
                      const nivelMin = Number(ed.nivelMinimo ?? 0);
                      const baseAllow = lvl >= nivelMin;
                      const override = (ed.escopo || {})[String(lvl)] || "";
                      const state = computePermCellState({ baseAllow, override });
                      const symbol = state === "DENY" ? "X" : state === "OWN" ? "P" : state === "ALLOW" ? "✓" : "";

                      const onToggle = () => {
                        const nextOverride = nextPermCellOverride({ baseAllow, currentOverride: override });
                        setPermEdit((prev) => {
                          const cur = prev[permFocusId] || ed;
                          const nextEscopo = { ...(cur.escopo || {}) };
                          const k = String(lvl);
                          if (!nextOverride) delete nextEscopo[k];
                          else nextEscopo[k] = nextOverride;
                          return {
                            ...prev,
                            [permFocusId]: { ...cur, escopo: nextEscopo },
                          };
                        });
                      };

                      return (
                        <div key={lvl} className="perm-scope-item">
                          <div className="perm-scope-label">{label}</div>
                          <button
                            type="button"
                            className={`perm-chip ${state === "ALLOW" ? "allow" : state === "OWN" ? "own" : state === "DENY" ? "deny" : "none"}`}
                            onClick={onToggle}
                            title={`${label} (${lvl})`}
                          >
                            {symbol}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="perm-foot">
                  <div className="perm-legend">
                    <span className="perm-legend-item"><span className="perm-chip allow" aria-hidden="true">✓</span>Permitido</span>
                    <span className="perm-legend-item"><span className="perm-chip own" aria-hidden="true">P</span>Próprias</span>
                    <span className="perm-legend-item"><span className="perm-chip deny" aria-hidden="true">X</span>Exceção</span>
                    <span className="perm-legend-item"><span className="perm-chip none" aria-hidden="true"></span>Sem acesso</span>
                  </div>
                </div>
              </div>
            )}

            <div className="perm-foot">
              <small className="perm-legend-note">Dica: use “Simular nível” para ver o acesso. Duplo clique para liberar/remover. Depois clique em Salvar.</small>
            </div>

            </>
            )}

          </section>
        )}
      </div>
    </LayoutDesktop>
  );
}

export default OpcoesPage;
